import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ProgressSteps from '../components/ProgressSteps';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CompleteProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser, setUser, setToken } = useAuth();
  
  // Get phone from location state or sessionStorage
  const phone = location.state?.phone || sessionStorage.getItem('signup_phone') || '';
  const isNewUser = location.state?.isNewUser || !user;
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate all fields
    const requiredFields = ['email', 'firstName', 'lastName', 'gender'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return;
      }
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        phone,
        isNewUser,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender
      };
      const response = await axios.post(
        `${API_URL}/signup-verification/basic-profile`,
        payload,
        {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setToken(response.data.token);
      }
      if (response.data.user) {
        setUser(response.data.user);
      }

      if (refreshUser) {
        await refreshUser();
      }
      navigate('/dashboard');
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || 'Profile update failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Complete Your Profile
          </h1>
        </div>

        {/* Progress Steps */}
        <ProgressSteps currentStep="profile" isMerchant={false} />

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(isNewUser ? '/signup' : '/login')}
          className="mb-4 flex items-center text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          <span className="text-sm">Back</span>
        </button>

        <h1 className="text-2xl font-semibold text-gray-900 mb-8">
          Create your LOCU account
        </h1>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Your email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900"
              placeholder="email@example.com"
              required
            />
          </div>

          {/* First Name */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">First name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900"
              placeholder="First name"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Last name</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900"
              placeholder="Last name"
              required
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date of birth</label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900"
            />
          </div>

          {/* Gender */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Gender</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900"
              required
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          {/* Terms */}
          <p className="text-xs text-gray-500 leading-relaxed">
            By clicking "Continue" you agree to the{' '}
            <a href="/terms" className="text-primary-600 hover:underline">Terms & Conditions</a>
            {' '}and{' '}
            <a href="/privacy" className="text-primary-600 hover:underline">Privacy Policy</a>.
          </p>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2D4A5E] text-white py-4 rounded-lg font-medium hover:bg-[#3D5A6E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </form>
      </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
